import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  formatCompetitorsOfTotal,
  formatInvestmentMultiple,
  formatPercent,
  type PersonaOpportunityDisplayRow,
} from "../../../lib/persona-opportunity-row-model.js";
import { personaOpportunityStatusLabel } from "../../../lib/persona-opportunity-tab-status.js";
import { PersonaOpportunitySupportingMedia } from "../../shared/PersonaOpportunitySupportingMedia/PersonaOpportunitySupportingMedia.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";

export interface PersonaOpportunityCardProps {
  row: PersonaOpportunityDisplayRow;
  selected?: boolean;
  selectionEnabled?: boolean;
  onToggleSelect?: (opportunityId: string) => void;
}

function KpiTile({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  tooltip: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="min-w-0 rounded-md bg-muted/40 px-1.5 py-2 text-center sm:px-2">
          <div className="text-[19px] font-medium tabular-nums leading-none text-foreground">
            {value}
          </div>
          <div className="mt-1.5 flex items-center justify-center gap-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            <span className="leading-tight">{label}</span>
            <Icon name="info" size="xs" className="size-2.5 shrink-0 opacity-60" aria-hidden />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[190px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export function PersonaOpportunityCard({
  row,
  selected = false,
  selectionEnabled = false,
  onToggleSelect,
}: PersonaOpportunityCardProps) {
  const { t } = useTranslation();
  const notReady = t(
    "personaOpportunities.workspace.actionNotReady",
    "Not available yet — coming in a later release.",
  );
  const investmentLabel = formatInvestmentMultiple(row.investmentIndex);
  const youPct = formatPercent(row.myInvestmentSharePct);
  const marketPct = formatPercent(row.marketInvestmentSharePct);
  const marketWidth = Math.min(100, Math.max(0, row.marketInvestmentSharePct * 100));
  const youWidth = Math.min(100, Math.max(0, row.myInvestmentSharePct * 100));
  const drivenFeatureLabel =
    row.drivenBy === "motivation"
      ? t("personaOpportunities.active.kpi.featureMotivation", "motivation")
      : row.drivenBy === "painpoint"
        ? t("personaOpportunities.active.kpi.featurePainPoint", "pain point")
        : t("personaOpportunities.active.kpi.featureAngle", "angle");

  return (
    <article className="flex flex-col gap-5 rounded-md border border-border bg-card p-[22px] transition-shadow hover:shadow-md">
      {/* Portrait media left / copy right — matches Opportunities v5 cards. */}
      <div className="flex flex-col gap-5 sm:flex-row sm:items-stretch sm:gap-6">
        <div className="relative mx-auto w-full max-w-[200px] shrink-0 sm:mx-0 sm:w-[176px] sm:self-stretch">
          <PersonaOpportunitySupportingMedia
            row={row}
            variant="card"
            className="sm:h-full sm:min-h-[280px] sm:aspect-auto"
          >
            <PersonaOpportunitySelectCheckbox
              variant="overlay"
              className="absolute left-2 top-2 z-10"
              checked={selected}
              disabled={!selectionEnabled}
              onToggle={() => onToggleSelect?.(row.id)}
            />
            <span className="pointer-events-none absolute right-2 top-2 z-[1] rounded-sm bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground">
              {personaOpportunityStatusLabel(t, row.status)}
            </span>
          </PersonaOpportunitySupportingMedia>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          <h3 className="text-base font-semibold leading-[1.35] text-foreground">{row.angle}</h3>
          <p className="line-clamp-3 text-[13px] text-muted-foreground" title={row.why}>
            {row.why}
          </p>
          <div className="space-y-1.5 text-xs text-muted-foreground">
            {row.painPoint ? (
              <p
                className={cn(
                  "rounded-sm px-1.5 py-1",
                  row.drivenBy === "painpoint" && "bg-primary/10 text-foreground",
                )}
              >
                <span className="font-medium uppercase tracking-wide text-muted-foreground/80">
                  {t("personaOpportunities.active.painPoint", "Pain point")}
                </span>
                {row.drivenBy === "painpoint" ? (
                  <span className="ml-1.5 inline-flex rounded-sm bg-primary/15 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    {t("personaOpportunities.active.drivenByBadge", "Driving")}
                  </span>
                ) : null}{" "}
                <span className={cn(row.drivenBy === "painpoint" && "font-semibold")}>
                  {row.painPoint}
                </span>
              </p>
            ) : null}
            {row.motivation ? (
              <p
                className={cn(
                  "rounded-sm px-1.5 py-1",
                  row.drivenBy === "motivation" && "bg-primary/10 text-foreground",
                )}
              >
                <span className="font-medium uppercase tracking-wide text-muted-foreground/80">
                  {t("personaOpportunities.active.motivation", "Motivation")}
                </span>
                {row.drivenBy === "motivation" ? (
                  <span className="ml-1.5 inline-flex rounded-sm bg-primary/15 px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    {t("personaOpportunities.active.drivenByBadge", "Driving")}
                  </span>
                ) : null}{" "}
                <span className={cn(row.drivenBy === "motivation" && "font-semibold")}>
                  {row.motivation}
                </span>
              </p>
            ) : null}
          </div>

          <div className="mt-auto space-y-3 border-t border-border pt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-[13.5px] font-semibold text-foreground">
                {investmentLabel
                  ? t("personaOpportunities.active.investmentHeadline", {
                      multiple: investmentLabel.replace(/\s*more$/i, ""),
                      defaultValue: "The market invests {{multiple}} more here than you do",
                    })
                  : t(
                      "personaOpportunities.active.investmentUnavailable",
                      "Investment index unavailable",
                    )}
              </p>
              <p className="text-xs text-muted-foreground">
                <span className="mr-2 inline-flex items-center gap-1">
                  <span className="size-2 rounded-sm bg-primary" aria-hidden />
                  {t("personaOpportunities.active.youShare", { pct: youPct, defaultValue: `You ${youPct}` })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="size-2 rounded-sm bg-violet-500" aria-hidden />
                  {t("personaOpportunities.active.marketShare", {
                    pct: marketPct,
                    defaultValue: `Market ${marketPct}`,
                  })}
                </span>
              </p>
            </div>

            <div className="relative h-2.5 overflow-hidden rounded-[5px] bg-muted">
              <div
                className="absolute inset-y-0 left-0 bg-violet-500/30"
                style={{ width: `${marketWidth}%` }}
              />
              <div
                className="absolute inset-y-0 left-0 bg-primary"
                style={{ width: `${youWidth}%` }}
              />
              <div
                className="absolute inset-y-0 w-0.5 bg-violet-500"
                style={{ left: `calc(${marketWidth}% - 1px)` }}
              />
            </div>

            <TooltipProvider delayDuration={200}>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <KpiTile
                  label={t("personaOpportunities.active.kpi.competitors", "Competitors")}
                  value={formatCompetitorsOfTotal(row.competitorsRunningCount, row.competitorsTotal)}
                  tooltip={t("personaOpportunities.active.kpi.competitorsTooltip", {
                    feature: drivenFeatureLabel,
                    defaultValue:
                      "How many selected competitors run creatives in this {{feature}} cluster.",
                  })}
                />
                <KpiTile
                  label={t("personaOpportunities.active.kpi.creatives", "Creatives")}
                  value={String(row.creativesCount)}
                  tooltip={t("personaOpportunities.active.kpi.creativesTooltip", {
                    feature: drivenFeatureLabel,
                    defaultValue: "Number of competitor creatives observed for this {{feature}}.",
                  })}
                />
                <KpiTile
                  label={t("personaOpportunities.active.kpi.stickRate", "Stick rate")}
                  value={formatPercent(row.stickRatePct)}
                  tooltip={t(
                    "personaOpportunities.active.kpi.stickRateTooltip",
                    "Share of creatives that stayed live through the stick window.",
                  )}
                />
                <KpiTile
                  label={t("personaOpportunities.active.kpi.duplicated", "Duplicated")}
                  value={formatPercent(row.duplicatedPct)}
                  tooltip={t("personaOpportunities.active.kpi.duplicatedTooltip", {
                    feature: drivenFeatureLabel,
                    defaultValue:
                      "Percent of creatives that were duplicated across different ads in the {{feature}} cluster.",
                  })}
                />
              </div>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 border-t border-border pt-4">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button type="button" variant="outline" size="sm" className="w-full" disabled>
                  {t("personaOpportunities.active.similarAngles", "See similar angles")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{notReady}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className={cn("w-full bg-primary/10 text-primary hover:bg-primary/15")}
                  disabled
                >
                  {t("personaOpportunities.active.generateScripts", "Generate scripts")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{notReady}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </article>
  );
}
